import _ from "lodash";
import { BaseDocument } from "./../../../@types/firestore";
import {
  ProcessedListingAddress,
  ProcessedListingData,
  rooms,
  parkingSpots,
  ListingOrigin,
  ListingListedFor
} from "./../../../@types/sharedSchema";
import { RequestOptions } from "@algolia/transporter";
import type { AlgoliaHit, SearchResponse } from "instantsearch.js";
import type { SearchOptions } from "@algolia/client-search";
import type { SearchIndex } from "algoliasearch";
import { SearchState } from "contexts/ListingsMatchContext";
import { getEpochTimeFromDaysAgo } from "utils/listingsMatch/data";
import { buildActiveDateRange } from "components/_dashboard/common/DateRangeFilter";
import type { REST_schema } from "./../../../@types/sharedSchema";
import {
  fetchNamesFacet,
  isPgSearchEnabled,
  postListingsSearch
} from "utils/listingsSearchApi";
import { tickedAmenities } from "utils/contacts/amenityLabels";

// SECURITY NOTE: org/ownership scoping for `webot` listings is enforced by
// Algolia via the embedded `filters` on the per-user secured API key the
// server hands us (see AlgoliaClientContext + the server's
// algoliaSecuredKey.service.ts). DO NOT re-add `originalListingOwnersId` /
// `originalListingOrganizationId` constraints here — they would just create
// drift risk with the canonical filter on the server.

function convertDatesSavedAsNumberToDate(obj: any) {
  const result = { ...obj };

  for (const key in obj) {
    if (obj[key] && typeof obj[key] === "number" && key.toLowerCase().includes("time")) {
      const value = obj[key];
      result[key] = new Date(value as number) as any;
    }
  }

  return result;
}

export function parseFirestoreObjectFromAlgolia(hit: AlgoliaHit<BaseDocument>) {
  return {
    ...convertDatesSavedAsNumberToDate(hit),
    id: hit?.objectID
  };
}

export type AlgoliaHitResult = AlgoliaHit<ProcessedListingData>;

// NOTE: by-id reads against the listings index used to live here. They've
// moved to `utils/listings/fetchListings` and now read straight from
// Firestore — Algolia is reserved for search and faceting.

export const getListingsMatchingFilters = async (
  index: SearchIndex,
  searchTerm: string,
  filterQueryString: string,
  maxResults = 30,
  page = 0,
  removeWordsIfNoResults = true
): Promise<SearchResponse<AlgoliaHit<Partial<BaseDocument> & ProcessedListingData>>> => {
  try {
    const results = await index.search<AlgoliaHit<ProcessedListingData & BaseDocument>>(
      searchTerm,
      {
        filters: filterQueryString,
        hitsPerPage: maxResults,
        page: page,
        highlightPreTag: "<mark>",
        highlightPostTag: "</mark>",
        removeWordsIfNoResults: removeWordsIfNoResults ? "allOptional" : "lastWords"
      }
    );

    if (!results || results.nbHits === 0) {
      // Zero hits for one (origin × location) sub-query is the common case
      // (e.g. webot-origin in a location where this realtor has no
      // listings). The outer fetchListings aggregates across many such
      // sub-queries, so this is normal and not actionable — don't log it.
      return null;
    }

    return results;
  } catch (err) {
    console.error("Algolia listings search failed", err);
    return null;
  }
};

export type PickerListingsSearch = {
  searchTerm: string;
  /** Undefined means both transaction types — a contract can be drawn on either. */
  listingListedFor?: ListingListedFor;
  /** Restricts to the realtor's own listings, for the pinned-on-top section. */
  onlyWebotOrigin?: boolean;
  maxResults?: number;
};

/**
 * Listing lookup for the signature pickers. They have no SearchState — they
 * only vary by origin and transaction type — so they'd otherwise have to hand
 * `getListingsMatchingFilters` a raw Algolia filter expression, which has no
 * meaning once search runs on Postgres. Returns hits directly (with `objectID`
 * populated) because that's all the pickers consume.
 */
export const searchListingsForPicker = async (
  index: SearchIndex,
  { searchTerm, listingListedFor, onlyWebotOrigin = false, maxResults = 30 }: PickerListingsSearch
): Promise<AlgoliaHitResult[]> => {
  if (isPgSearchEnabled()) {
    // The server requires a transaction type, so "both" is two requests merged.
    // Each gets its own slice of the budget, otherwise the sale results would
    // fill the list and the rentals would never be reachable.
    const listedForValues: ListingListedFor[] = listingListedFor
      ? [listingListedFor]
      : ["sale", "rent"];
    const perRequest = Math.max(1, Math.ceil(maxResults / listedForValues.length));

    const responses = await Promise.all(
      listedForValues.map((listedFor) =>
        postListingsSearch({
          listingListedFor: listedFor,
          maxResults: perRequest,
          // A picker's term is an ADDRESS (Trello #254): read in the street / neighbourhood /
          // city, quote marks ignored, best address match first, never relaxed to any word.
          ...(searchTerm?.trim() ? { searchTerm: searchTerm.trim(), searchTermMatch: "address" as const } : {}),
          ...(onlyWebotOrigin ? { listingOrigins: ["webot" as ListingOrigin] } : {})
        })
      )
    );

    return responses
      .flatMap((response) => response.hits)
      .map(
        (hit) =>
          ({ ...convertDatesSavedAsNumberToDate(hit), objectID: hit.id }) as unknown as AlgoliaHitResult
      )
      .slice(0, maxResults);
  }

  const filters = combineFilters(
    onlyWebotOrigin ? createOriginFilter("webot") : "",
    createListedForFilter(listingListedFor)
  );

  const results = await getListingsMatchingFilters(index, searchTerm, filters, maxResults);

  return (results?.hits ?? []) as AlgoliaHitResult[];
};

/**
 * Maps the UI SearchState onto the server's ListingsSearchRequest, replicating
 * the exact per-field guards createSearchFilter applies before emitting an
 * Algolia term (e.g. maxRooms at the slider maximum means "no cap").
 */
const buildPgSearchRequest = (
  searchState: SearchState,
  maxResults: number,
  page: number
): REST_schema.ListingsSearchRequest => {
  const request: REST_schema.ListingsSearchRequest = {
    listingListedFor: searchState.listingListedFor,
    maxResults: Math.min(Math.max(Math.ceil(maxResults), 1), 300),
    page
  };

  if (searchState.searchTerm?.trim()) request.searchTerm = searchState.searchTerm.trim();

  if (searchState.locations?.length) {
    request.locations = searchState.locations.map((location) =>
      _.omitBy(
        {
          city: location.city,
          neighborhood: location.neighborhood,
          street: location.street,
          canonicalLocationId: location.canonicalLocationId,
          canonicalLevel: location.canonicalLevel
        },
        _.isUndefined
      )
    );
  }

  if (searchState.listingOrigins?.length) request.listingOrigins = searchState.listingOrigins;

  // typeof-number guards, not `!isNaN(...)`: isNaN coerces, so a STRING value from a
  // poisoned contact ("3", or even "") passed these checks, rode into the request and
  // failed the endpoint's zod z.number() — a silent 400 rendering as zero results.
  if (typeof searchState.minRooms === "number" && !isNaN(searchState.minRooms) && searchState.minRooms >= 0) {
    request.minRooms = searchState.minRooms;
  }
  if (
    typeof searchState.maxRooms === "number" &&
    !isNaN(searchState.maxRooms) &&
    searchState.maxRooms >= 0 &&
    searchState.maxRooms < rooms[rooms.length - 1]
  ) {
    request.maxRooms = searchState.maxRooms;
  }
  if (typeof searchState.minFloors === "number" && !isNaN(searchState.minFloors) && searchState.minFloors >= -4) {
    request.minFloor = searchState.minFloors;
  }
  if (typeof searchState.maxFloors === "number" && !isNaN(searchState.maxFloors) && searchState.maxFloors <= 50) {
    request.maxFloor = searchState.maxFloors;
  }
  if (
    !isNaN(searchState.minParkingSpots) &&
    !_.isNil(searchState.minParkingSpots) &&
    searchState.minParkingSpots >= 0
  ) {
    request.minParkingSpots = searchState.minParkingSpots;
  }
  if (
    !isNaN(searchState.maxParkingSpots) &&
    !_.isNil(searchState.maxParkingSpots) &&
    searchState.maxParkingSpots >= 0 &&
    searchState.maxParkingSpots < parkingSpots[parkingSpots.length - 1]
  ) {
    request.maxParkingSpots = searchState.maxParkingSpots;
  }
  // Built area. `> 0`, not `>= 0`: a 0 is an unset bound, and `square_meter >= 0` would still
  // drop the listings that state no size at all for a filter the realtor never set.
  if (typeof searchState.minSquareMeter === "number" && searchState.minSquareMeter > 0) {
    request.minSquareMeter = searchState.minSquareMeter;
  }
  if (typeof searchState.maxSquareMeter === "number" && searchState.maxSquareMeter > 0) {
    request.maxSquareMeter = searchState.maxSquareMeter;
  }
  if (_.isNumber(searchState.minPrice) && !isNaN(searchState.minPrice) && searchState.minPrice >= 0) {
    request.minPrice = searchState.minPrice;
  }
  if (_.isNumber(searchState.maxPrice) && !isNaN(searchState.maxPrice) && searchState.maxPrice >= 0) {
    request.maxPrice = searchState.maxPrice;
  }
  // Only meaningful alongside a bound — the server ignores it otherwise, and sending
  // it anyway would make two identical searches look different in the audit log.
  if (
    searchState.includeListingsWithoutPrice &&
    (request.minPrice !== undefined || request.maxPrice !== undefined)
  ) {
    request.includeListingsWithoutPrice = true;
  }

  if (searchState.agencyNames?.length) request.agencyNames = searchState.agencyNames;
  if (searchState.sellerNames?.length) request.sellerNames = searchState.sellerNames;
  if (searchState.propertyTypes?.length) request.propertyTypes = searchState.propertyTypes;
  if (searchState.adTypes?.length) request.adTypes = searchState.adTypes;

  // A real `true` or not sent: the endpoint refuses the whole search (400) over one value that
  // is not a boolean, and a refused search reads as "no listings". See `tickedAmenities`.
  const booleanFilters = tickedAmenities(searchState.booleanPropertiesDetails);
  if (Object.keys(booleanFilters).length) request.booleanFilters = booleanFilters;

  if (searchState.isAssetExclusive) request.isAssetExclusive = true;
  if (searchState.onlyPriceChanged) request.onlyPriceChanged = true;
  // The webot-origin exemption is enforced server-side.
  if (searchState.hidePotentiallyRemovedListings) request.hidePotentiallyRemovedListings = true;

  if (searchState.publishedDateRange) {
    const { from, to } = buildActiveDateRange(
      searchState.publishedDateRange.preset,
      searchState.publishedDateRange.customFrom,
      searchState.publishedDateRange.customTo
    );
    if (from) request.publishedFromMs = from.getTime();
    if (to) request.publishedToMs = to.getTime();
  }

  return request;
};

const fetchListingsViaPgSearch = async (
  searchState: SearchState,
  maxResults: number,
  page: number
): Promise<{ hits: AlgoliaHitResult[]; totalHits: number; totalHitsCapped?: boolean }> => {
  const response = await postListingsSearch(buildPgSearchRequest(searchState, maxResults, page));
  // Hits carry epoch-ms dates (same wire shape Algolia hits had) — reuse the
  // same conversion, and keep `objectID` populated: dedupe/selection all key on it.
  const hits = response.hits.map(
    (hit) => ({ ...convertDatesSavedAsNumberToDate(hit), objectID: hit.id }) as unknown as AlgoliaHitResult
  );
  // `totalHitsCapped` means the true total is at or above the server's 10k
  // count cap, so the UI shows "10,000+" rather than a number that reads exact.
  return { hits, totalHits: response.totalHits, totalHitsCapped: response.totalHitsCapped };
};

/**
 * Main function to fetch listings. With USE_POSTGRES_LISTINGS_SEARCH the whole
 * search is ONE server request (Postgres, globally sorted, server-side
 * scoping); otherwise the legacy Algolia path fans out one query per
 * (origin × location) and combines the results.
 */
export const fetchListings = async (
  index: SearchIndex,
  searchState: SearchState,
  maxResults: number = 300,
  page: number = 0
): Promise<{ hits: AlgoliaHitResult[]; totalHits: number; totalHitsCapped?: boolean }> => {
  if (isPgSearchEnabled()) {
    return fetchListingsViaPgSearch(searchState, maxResults, page);
  }

  let hits: AlgoliaHitResult[] = [];
  let totalHits: number = 0;

  const searchFilter = createSearchFilter(searchState);

  for (const origin of searchState.listingOrigins) {
    // Hide potentially-removed (stale) listings only for third-party origins.
    // webot listings are the realtor's own and can legitimately go a long time
    // without an update, so they must never be hidden for being "old".
    const staleListingFilter =
      searchState.hidePotentiallyRemovedListings && origin !== "webot"
        ? `updatedTime >= ${getEpochTimeFromDaysAgo(3)}`
        : "";

    const originFilter = combineFilters(createOriginFilter(origin), staleListingFilter);

    const { hits: hitsFromOrigin, totalHits: totalHitsFromOrigin } =
      await searchListingsAcrossLocations(
        index,
        searchState.searchTerm,
        searchState.locations,
        searchFilter,
        originFilter,
        maxResults / 3,
        page
      );

    hits.push(...hitsFromOrigin);

    totalHits += totalHitsFromOrigin;
  }

  return {
    hits,
    totalHits
  };
};

/**
 * Function to search listings across one or multiple locations
 */
const searchListingsAcrossLocations = async (
  index: SearchIndex,
  searchTerm: string,
  locations: ProcessedListingAddress[],
  searchFilter: string,
  searchOriginFilter: string,
  maxTotalResults: number,
  page: number
): Promise<{ hits: AlgoliaHitResult[]; totalHits: number }> => {
  const maxPerLocation = Math.max(1, Math.ceil(maxTotalResults / Math.max(1, locations.length)));

  const searchLocations = !locations || locations.length === 0 ? [null] : locations;

  const searchPromises = searchLocations.map(async (location) => {
    const filter = combineFilters(
      searchFilter,
      searchOriginFilter,
      location ? createLocationFilter(location) : ""
    );

    const response = await getListingsMatchingFilters(
      index,
      searchTerm,
      filter,
      maxPerLocation,
      page,
      true
    );

    return response;
  });

  const results = await Promise.all(searchPromises);

  let hits: AlgoliaHitResult[] = [];
  let totalHits = 0;

  results.forEach((result) => {
    if (result) {
      totalHits += result.nbHits;

      const processedHits = result.hits.map((hit) =>
        parseFirestoreObjectFromAlgolia(hit as any)
      ) as AlgoliaHitResult[];
      hits.push(...processedHits);
    }
  });

  return { hits, totalHits };
};

export const getAgencyNamesRefinementList = async (
  index: SearchIndex,
  search: string = "",
  page: number = 0,
  hitsPerPage: number = 100,
  listingOrigin?: ListingOrigin
) => {
  const filters = listingOrigin ? `listingOrigin:${listingOrigin}` : "";

  try {
    if (isPgSearchEnabled()) {
      const { names } = await fetchNamesFacet("agency-names", search, listingOrigin);
      return names;
    }

    const results = await index.search(search, {
      filters,
      facets: ["agencyName"],
      maxValuesPerFacet: hitsPerPage,
      page
    });

    const facetData = results.facets?.agencyName ?? {};
    return Object.keys(facetData);
  } catch (error) {
    console.error("Error fetching agency names facet:", error);
    return [];
  }
};

export const getSellerNamesRefinementList = async (
  index: SearchIndex,
  search: string = "",
  page: number = 0,
  hitsPerPage: number = 100,
  listingOrigin?: ListingOrigin
) => {
  const filters = listingOrigin ? `listingOrigin:${listingOrigin}` : "";

  try {
    if (isPgSearchEnabled()) {
      const { names } = await fetchNamesFacet("seller-names", search, listingOrigin);
      return names;
    }

    const results = await index.search(search, {
      filters,
      facets: ["sellerName"],
      maxValuesPerFacet: hitsPerPage,
      page
    });

    const facetData = results.facets?.sellerName ?? {};
    return Object.keys(facetData);
  } catch (error) {
    console.error("Error fetching seller names facet:", error);
    return [];
  }
};

export const getCitiesRefinementList = async (index: SearchIndex) => {
  try {
    const results = await index.searchForFacetValues("address.city", "", {
      maxFacetHits: 100
    });

    return results.facetHits;
  } catch (error) {
    console.error("Error fetching cities facet:", error);
    return [];
  }
};

export type AlgoliaResultType = "city" | "neighborhood" | "street" | "any";

type GetLocationAutocompleteSuggestionsOptions = {
  index: SearchIndex;
  query: string;
  resultType?: AlgoliaResultType;
  includeAddressCoords?: boolean;
  additionalParameters?: RequestOptions & SearchOptions;
  removeNeighborhoodForStreetMatches?: boolean;
};

export const getLocationAutocompleteSuggestions = async ({
  index,
  query,
  resultType = "any",
  includeAddressCoords = false,
  additionalParameters = {},
  removeNeighborhoodForStreetMatches = true
}: GetLocationAutocompleteSuggestionsOptions): Promise<ProcessedListingAddress[]> => {
  const defaultLocationAttributesToSearch = [
    "address.city",
    "address.neighborhood",
    "address.street"
  ];

  const defaultLocationAttributesToRetrieve = [
    "address.city",
    "address.neighborhood",
    "address.street"
  ];

  if (includeAddressCoords) {
    defaultLocationAttributesToRetrieve.push("address.coords");
  }

  try {
    const { hits } = await index.search(query, {
      attributesToRetrieve: defaultLocationAttributesToRetrieve,
      restrictSearchableAttributes: defaultLocationAttributesToSearch,
      hitsPerPage: 50,
      distinct: true,
      ...additionalParameters
    });

    let suggestions: any[] = [];

    hits.forEach((hit: any) => {
      const city = hit.address?.city?.trim();
      const street = hit.address?.street?.trim();
      const neighborhood = hit.address?.neighborhood?.trim();

      const cityMatch = city && hit._highlightResult.address?.city?.matchLevel !== "none";

      const streetMatch = street && hit._highlightResult.address?.street?.matchLevel !== "none";

      const neighborhoodMatch =
        neighborhood && hit._highlightResult.address?.neighborhood?.matchLevel !== "none";

      let matchedAddress: Partial<ProcessedListingAddress> = {};

      if (includeAddressCoords) {
        matchedAddress.coords = hit.address?.coords;
      }

      if (cityMatch && (resultType === "city" || resultType === "any")) {
        matchedAddress.city = city;

        suggestions.push(_.omitBy(matchedAddress, _.isUndefined));
      }

      if (neighborhoodMatch && (resultType === "neighborhood" || resultType === "any")) {
        matchedAddress.city = city;
        matchedAddress.neighborhood = neighborhood;

        suggestions.push(_.omitBy(matchedAddress, _.isUndefined));
      }

      if (streetMatch && (resultType === "street" || resultType === "any")) {
        if (removeNeighborhoodForStreetMatches) {
          matchedAddress.city = city;
          matchedAddress.street = street;

          suggestions.push(_.omitBy(matchedAddress, _.isUndefined));
        } else {
          matchedAddress.city = city;
          matchedAddress.street = street;
          matchedAddress.neighborhood = neighborhood;

          suggestions.push(_.omitBy(matchedAddress, _.isUndefined));
        }
      }
    });

    suggestions = _.uniqWith(suggestions, (aHit, bHit) => {
      const hasSameStreet = (aHit.street ?? "") === (bHit.street ?? "");
      const hasSameNeighborhood = (aHit.neighborhood ?? "") === (bHit.neighborhood ?? "");
      const hasSameCity = (aHit.city ?? "") === (bHit.city ?? "");

      return hasSameStreet && hasSameNeighborhood && hasSameCity;
    });

    return suggestions;
  } catch (error) {
    console.error("Error fetching autocomplete suggestions:", error);
    return [];
  }
};

// Origin filter is now purely a UX selector. Ownership / org scoping is
// enforced by the secured key the server hands us — see the module-level
// security note at the top.
const createOriginFilter = (origin: ListingOrigin): string => `listingOrigin:${origin}`;

// "sale" is expressed as NOT rent because a listing without an explicit
// `listingListedFor` is a sale by default. Undefined means no filter at all.
const createListedForFilter = (listedFor?: ListingListedFor): string => {
  if (listedFor === "rent") return `listingListedFor:"rent"`;
  if (listedFor === "sale") return `NOT listingListedFor:"rent"`;
  return "";
};

const createSearchFilter = (searchState: SearchState): string => {
  const phraseFacetTerms: string[] = [];

  if (!isNaN(searchState.minRooms) && !_.isNil(searchState.minRooms) && searchState.minRooms >= 0) {
    phraseFacetTerms.push(`roomsCount >= ${searchState.minRooms}`);
  }

  if (
    !isNaN(searchState.maxRooms) &&
    !_.isNil(searchState.maxRooms) &&
    searchState.maxRooms >= 0 &&
    searchState.maxRooms < rooms[rooms.length - 1]
  ) {
    phraseFacetTerms.push(`roomsCount <= ${searchState.maxRooms}`);
  }

  if (
    !isNaN(searchState.minFloors) &&
    !_.isNil(searchState.minFloors) &&
    searchState.minFloors >= -4
  ) {
    phraseFacetTerms.push(`address.floor >= ${searchState.minFloors}`);
  }

  if (
    !isNaN(searchState.maxFloors) &&
    !_.isNil(searchState.maxFloors) &&
    searchState.maxFloors <= 50
  ) {
    phraseFacetTerms.push(`address.floor <= ${searchState.maxFloors}`);
  }

  if (
    !isNaN(searchState.minParkingSpots) &&
    !_.isNil(searchState.minParkingSpots) &&
    searchState.minParkingSpots >= 0
  ) {
    phraseFacetTerms.push(`parkingSpotsCount >= ${searchState.minParkingSpots}`);
  }

  if (
    !isNaN(searchState.maxParkingSpots) &&
    !_.isNil(searchState.maxParkingSpots) &&
    searchState.maxParkingSpots >= 0 &&
    searchState.maxParkingSpots < parkingSpots[parkingSpots.length - 1]
  ) {
    phraseFacetTerms.push(`parkingSpotsCount <= ${searchState.maxParkingSpots}`);
  }

  if (
    _.isNumber(searchState.minPrice) &&
    !isNaN(searchState.minPrice) &&
    searchState.minPrice !== undefined &&
    searchState.minPrice >= 0
  ) {
    phraseFacetTerms.push(`price >= ${searchState.minPrice}`);
  }

  if (
    _.isNumber(searchState.maxPrice) &&
    !isNaN(searchState.maxPrice) &&
    searchState.maxPrice !== undefined &&
    searchState.maxPrice >= 0
  ) {
    phraseFacetTerms.push(`price <= ${searchState.maxPrice}`);
  }

  if (searchState.agencyNames && searchState.agencyNames.length > 0) {
    const agencyNamesTerms = searchState.agencyNames.map(
      (agencyName) => `(agencyName:"${escapeAlgoliaFilterValue(agencyName)}")`
    );

    phraseFacetTerms.push(agencyNamesTerms.join(" OR "));
  }

  if (searchState.sellerNames && searchState.sellerNames.length > 0) {
    const sellerNamesTerms = searchState.sellerNames.map(
      (sellerName) => `(sellerName:"${escapeAlgoliaFilterValue(sellerName)}")`
    );

    phraseFacetTerms.push(sellerNamesTerms.join(" OR "));
  }

  if (searchState.propertyTypes && searchState.propertyTypes.length > 0) {
    const propertyTypesTerms = searchState.propertyTypes.map((s) => `(propertyType:"${s}")`);
    phraseFacetTerms.push(propertyTypesTerms.join(" OR "));
  }

  if (searchState.adTypes && searchState.adTypes.length > 0) {
    const adTypesTerms = searchState.adTypes.map((s) => `(adType:"${s}")`);
    phraseFacetTerms.push(adTypesTerms.join(" OR "));
  }

  if (searchState.listingListedFor) {
    if (searchState.listingListedFor === "rent") {
      phraseFacetTerms.push(`listingListedFor:"rent"`);
    } else {
      phraseFacetTerms.push(`NOT listingListedFor:"rent"`);
    }
  }

  for (const [key, value] of Object.entries(searchState.booleanPropertiesDetails ?? {})) {
    if (value) {
      phraseFacetTerms.push(`${key}:"${value}"`);
    }
  }

  // NOTE: the `hidePotentiallyRemovedListings` staleness filter is intentionally
  // NOT applied here. It must skip webot (own) listings, which legitimately go long
  // stretches without an update, and Algolia's `filters` parser can't express that
  // exemption inline (no mixing numeric + facet in one OR, no negating a group).
  // It's applied per-origin in `fetchListings` instead, where webot is excluded.

  if (searchState.isAssetExclusive) {
    phraseFacetTerms.push(`isAssetExclusive:"true"`);
  }

  if (searchState.onlyPriceChanged) {
    phraseFacetTerms.push(`lastPriceChangeDate > 0`);
  }

  if (searchState.publishedDateRange) {
    const { from, to } = buildActiveDateRange(
      searchState.publishedDateRange.preset,
      searchState.publishedDateRange.customFrom,
      searchState.publishedDateRange.customTo
    );
    if (from) {
      const fromMs = from.getTime();
      phraseFacetTerms.push(`(listingPublishedTime >= ${fromMs} OR createdTime >= ${fromMs})`);
    }
    if (to) {
      const toMs = to.getTime();
      phraseFacetTerms.push(`(listingPublishedTime <= ${toMs} OR createdTime <= ${toMs})`);
    }
  }

  return phraseFacetTerms.length > 0 ? phraseFacetTerms.join(" AND ") : "";
};

/**
 * Filter by the canonical master id instead of the raw address spelling, so a picked
 * master returns ALL its listings regardless of which source spelling each carries
 * (Madlan's "רמת נבון" and Yad2's "רמת נבון . הר כרמי" both map to one master).
 *
 * ENABLED (2026-07-27): the Algolia index now facets `canonicalLocation.*`
 * (`filterOnly`). Kept as a flag, in lockstep with the server flag of the same name
 * (algolia.service.ts), so both can be flipped together if the index config changes.
 * A location WITHOUT a canonical id (manually-typed address, pre-canonical saved pick)
 * still falls back to the raw-string facet.
 */
const CANONICAL_ID_FACET_ENABLED = true;

const createLocationFilter = (location: ProcessedListingAddress): string => {
  // id-facet for neighborhood/street — the levels where source spellings diverge and the
  // stamp recovers all of them. A neighborhood pick ALSO matches child-neighborhood
  // listings via the denormalized parent id (parity with the alert matcher). City keeps
  // the raw facet: city text is single-spelling (semel-backed) and complete, whereas the
  // id-facet would drop the tiny unstamped tail.
  if (CANONICAL_ID_FACET_ENABLED && location.canonicalLocationId) {
    const id = location.canonicalLocationId.replaceAll('"', '\\"');
    if (location.canonicalLevel === "neighborhood") {
      return `(canonicalLocation.neighborhoodId:"${id}" OR canonicalLocation.neighborhoodParentId:"${id}")`;
    }
    if (location.canonicalLevel === "street") {
      return `canonicalLocation.streetId:"${id}"`;
    }
  }

  const locationParts: string[] = [];

  if (location.city) {
    locationParts.push(`address.city:"${location.city.replaceAll('"', '\\"')}"`);
  }
  if (location.neighborhood) {
    locationParts.push(`address.neighborhood:"${location.neighborhood.replaceAll('"', '\\"')}"`);
  }
  if (location.street) {
    locationParts.push(`address.street:"${location.street.replaceAll('"', '\\"')}"`);
  }

  return locationParts.length > 0 ? locationParts.join(" AND ") : "";
};

const combineFilters = (...filters: string[]): string => {
  const validFilters = filters.filter((f) => f && f.trim());
  return validFilters.length > 0 ? validFilters.join(" AND ") : "";
};

const escapeAlgoliaFilterValue = (value: string): string => {
  const charactersToReplace = ["‘", '"'];

  let escapedValue = value;

  for (const character of charactersToReplace) {
    escapedValue = escapedValue.replaceAll(character, `\\${character}`);
  }

  return escapedValue;
};
